import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Box, Breadcrumbs, Link as MuiLink, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";

import { usePageTitleSlot } from "./pageTitleSlot";

/**
 * 每一頁的頂部資訊，分成兩個位置：
 *
 *   頂列（漢堡右邊）   大標 + 一行副標
 *   頁面最上方那條帶子  麵包屑（只有麵包屑）
 *
 * 標題放到頂列是為了把垂直空間還給內容 —— 原本大標自己佔掉快 100px，而
 * 頂列右半邊本來是空的。標題會用 portal 畫進頂列預留的那一格（見
 * `pageTitleSlot.ts`），所以宣告的地方仍然只有這裡一處。
 *
 * 切換面向的 tab **不在這裡** —— 它由 `AppShell` 畫在頂列與這條帶子之間，
 * 自己佔滿一行（見 `ServicePageTabs`）。
 *
 * **這條帶子刻意只放麵包屑，而且高度是固定的 44px。** 它與 tab 列合起來是
 * 導覽用的框架，框架不該隨頁面內容變高變矮 —— 早期版本把頁面動作
 * （例如「新增品項」）放在這裡，按鈕比麵包屑高，於是有動作的頁面整條帶子
 * 就比沒有動作的高一截，切換 tab 時下面的內容會上下跳動。
 *
 * 頁面自己的動作請放進頁面內容的第一列（例如品項管理放在篩選那一列的右邊），
 * 那裡本來就是可以隨頁面變化的區域。所以這個元件**沒有** actions 這個 prop：
 * 拿掉的不只是那個按鈕，是「動作能放進框架」這個可能性。
 *
 * 抽成共用元件是為了讓七個服務的頁面自動長得一樣 —— 否則每個服務各自寫
 * 一份標題區塊，間距與字級很快就會漂掉。
 */

export interface Crumb {
  label: string;
  /** 沒有 to 的就是目前所在的位置（不可點） */
  to?: string;
}

interface PageHeaderProps {
  crumbs: Crumb[];
  title: string;
  description?: ReactNode;
}

/** 畫在頂列裡的那一塊。h1 也在這裡 —— 一頁只有一個 h1，它就是頁面標題 */
function TopBarTitle({ title, description }: { title: string; description?: ReactNode }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle1" component="h1" noWrap sx={{ fontWeight: 700, lineHeight: 1.3 }}>
        {title}
      </Typography>
      {description && (
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{
            // 手機的頂列還要放標誌與帳號，副標會把標題擠掉，所以只在
            // sm 以上顯示 —— 副標是輔助資訊，標題不是
            display: { xs: "none", sm: "block" },
            lineHeight: 1.4,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
}

export function PageHeader({ crumbs, title, description }: PageHeaderProps) {
  const slot = usePageTitleSlot();

  return (
    <>
      {slot && createPortal(<TopBarTitle title={title} description={description} />, slot)}

      <Box
        sx={{
          flex: "none",
          // 固定高度而不是 minHeight：這條帶子是框架的一部分，不可以隨頁面
          // 內容變高（理由見上面的說明）
          height: 44,
          px: 3,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Breadcrumbs
          separator={<NavigateNextIcon sx={{ fontSize: 16 }} />}
          aria-label="麵包屑"
          sx={{
            flex: "none",
            "& .MuiBreadcrumbs-separator": { mx: 0.75, color: "text.secondary" },
          }}
        >
          {crumbs.map((crumb) =>
            crumb.to ? (
              <MuiLink
                key={crumb.label}
                component={Link}
                to={crumb.to}
                underline="hover"
                variant="body2"
                sx={{ color: "text.secondary" }}
              >
                {crumb.label}
              </MuiLink>
            ) : (
              <Typography key={crumb.label} variant="body2" sx={{ fontWeight: 600 }}>
                {crumb.label}
              </Typography>
            ),
          )}
        </Breadcrumbs>
      </Box>
    </>
  );
}
